import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

interface SensorStatusFlagsProps {
  billable: boolean;
  maintenance: boolean;
  archived?: boolean;
  /**
   * #1182 — EFFECTIVE calibration scope. The stored column is tri-state
   * (NULL = inherit the account's Calibration tag), but the control is a plain
   * checkbox, so the caller passes the already-resolved boolean.
   */
  calibrationIncluded?: boolean;
  /** #1182 — true when the value above is inherited rather than set on the sensor. */
  calibrationIsInherited?: boolean;
  /**
   * #1118 — per-sensor exception to the account's all-or-nothing Onsite calibration
   * flag. 'inherit' is the UI sentinel for a stored NULL ("follow the account").
   */
  onsiteCalibrationOverride?: 'inherit' | 'onsite' | 'ship';
  isSuperAdmin: boolean;
  /**
   * Widened from boolean-only: the #1118 on-site exception is a 3-value string, not a
   * flag. Both dialogs already pass a handler taking a wider union, so this is a
   * type-only change.
   */
  onChange: (field: string, value: boolean | string) => void;
  /** Hide the archived checkbox (e.g. in create mode) */
  hideArchived?: boolean;
  /** Hide the calibration checkbox (e.g. in create mode, where NULL = inherit is correct) */
  hideCalibration?: boolean;
}

export function SensorStatusFlags({
  billable,
  maintenance,
  archived = false,
  calibrationIncluded = false,
  calibrationIsInherited = false,
  onsiteCalibrationOverride = 'inherit',
  isSuperAdmin,
  onChange,
  hideArchived,
  hideCalibration,
}: SensorStatusFlagsProps) {
  const { t } = useTranslation('operations');

  const handleArchivedChange = (checked: boolean) => {
    onChange('archived', checked);
    if (checked) {
      onChange('maintenance', true);
    }
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        {t('sensorFlags.title', 'Status Flags')}
      </h4>
      <div className="space-y-3">
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="sensor-billable"
              checked={billable}
              onCheckedChange={(checked) => onChange('billable', !!checked)}
            />
            <Label htmlFor="sensor-billable" className="text-sm font-normal">
              {t('sensorFlags.billable', 'Billable')}
            </Label>
          </div>
        )}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Checkbox
              id="sensor-maintenance"
              checked={maintenance}
              onCheckedChange={(checked) => onChange('maintenance', !!checked)}
            />
            <Label htmlFor="sensor-maintenance" className="text-sm font-normal">
              {t('sensorFlags.maintenance', 'In Maintenance')}
            </Label>
          </div>
          <p className="text-xs text-muted-foreground ml-6">
            {t(
              'sensorFlags.maintenanceHelp',
              'When in maintenance, data is not collected and alerts are not triggered.',
            )}
          </p>
        </div>
        {!hideCalibration && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Checkbox
                id="sensor-calibration"
                checked={calibrationIncluded}
                onCheckedChange={(checked) => onChange('calibrationIncluded', !!checked)}
              />
              <Label htmlFor="sensor-calibration" className="text-sm font-normal">
                {t('sensorFlags.calibration', 'Calibration')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground ml-6">
              {t(
                'sensorFlags.calibrationHelp',
                'When unchecked, this sensor is left out of the Calibration Planning expired and upcoming lists. Use it for equipment the customer calibrates themselves.',
              )}
              {calibrationIsInherited
                ? ` ${t(
                  'sensorFlags.calibrationInherited',
                  "Currently inherited from the account's Calibration tag.",
                )}`
                : ''}
            </p>

            {/*
              #1118 — the per-sensor exception to the account's all-or-nothing Onsite
              flag. Only offered when the sensor IS calibrated: asking "on site or
              shipped?" about a sensor we do not calibrate is meaningless.
            */}
            {calibrationIncluded && (
              <div className="ml-6 space-y-1 pt-1">
                <Label htmlFor="sensor-onsite-override" className="text-xs font-normal text-muted-foreground">
                  {t('sensorFlags.onsiteOverride', 'Calibration location')}
                </Label>
                <Select
                  value={onsiteCalibrationOverride}
                  onValueChange={(value) => onChange('onsiteCalibrationOverride', value)}
                >
                  <SelectTrigger id="sensor-onsite-override" className="h-7 w-56 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">
                      {t('sensorFlags.onsiteInherit', 'Account default')}
                    </SelectItem>
                    <SelectItem value="onsite">
                      {t('sensorFlags.onsiteOnsite', 'On site (exception)')}
                    </SelectItem>
                    <SelectItem value="ship">
                      {t('sensorFlags.onsiteShip', 'Shipped (exception)')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'sensorFlags.onsiteOverrideHelp',
                    'The account decides on site vs shipped for its whole fleet. Set an exception here for this sensor only — for example a -80 freezer a technician must visit on an account that otherwise ships.',
                  )}
                </p>
              </div>
            )}
          </div>
        )}
        {!hideArchived && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Checkbox
                id="sensor-archived"
                checked={archived}
                onCheckedChange={handleArchivedChange}
              />
              <Label htmlFor="sensor-archived" className="text-sm font-normal">
                {t('sensorFlags.archived', 'Archived')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground ml-6">
              {t(
                'sensorFlags.archivedHelp',
                'Archive a sensor to hide it from view. All data and configurations are kept intact.',
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
